import { useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientClients,
  selectCrossClientFilteredResults,
  selectCrossClientResultsExecution,
  selectCrossClientTeammates,
  selectCrossClientUsersLoading,
} from '../../../store/crossClient/crossClientResults'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { CrossClientQueryResultDisplay, CrossClientQueryResultMatches } from '../../../models/CrossClient'
import { useCallback } from 'react'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { getOwnerLabel } from '../utils'

export const useExportCrossClientResults = () => {
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const results = useAppSelector(selectCrossClientFilteredResults)
  const clients = useAppSelector(selectCrossClientClients)
  const teammates = useAppSelector(selectCrossClientTeammates)
  const usersLoading = useAppSelector(selectCrossClientUsersLoading)

  const disabled = usersLoading || !results.length

  const exportCSV = async () => {
    if (disabled) {
      return
    }

    const csvRows = [
      ['Client', 'Owner', 'Match'],
      ...results.map((result) => {
        const client = clients[result.clientId]
        const clientName = client ? getUserDisplayName(client) : 'Unknown'

        const ownerId = client?.owner?.ownerId
        const owner = ownerId ? teammates[ownerId] : null
        const ownerName = owner ? getUserDisplayName(owner) : 'Unknown'
        const match = result.match

        return [clientName, ownerName, match]
      }),
    ]

    const csvContent = csvRows.map((row) => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `CrossClientResults_${execution?.id}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return {
    disabled,
    exportCSV,
  }
}

const matchOrderMap = {
  [CrossClientQueryResultMatches.STRONG]: 1,
  [CrossClientQueryResultMatches.PARTIAL]: 2,
  [CrossClientQueryResultMatches.WEAK]: 3,
}

export const useSortCrossClientResults = () => {
  const clients = useAppSelector(selectCrossClientClients)
  const teammates = useAppSelector(selectCrossClientTeammates)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const sortCrossClientResultsByClient = useCallback(
    (result1: CrossClientQueryResultDisplay, result2: CrossClientQueryResultDisplay) => {
      const client1 = clients[result1.clientId]
      const client2 = clients[result2.clientId]
      return getUserDisplayName(client1).localeCompare(getUserDisplayName(client2))
    },
    [clients],
  )

  const sortCrossClientResultsByOwner = useCallback(
    (result1: CrossClientQueryResultDisplay, result2: CrossClientQueryResultDisplay) => {
      const client1 = clients[result1.clientId]
      const client2 = clients[result2.clientId]
      const ownerId1 = client1?.owner?.ownerId ?? null
      const ownerId2 = client2?.owner?.ownerId ?? null
      const ownerLabel1 = getOwnerLabel(currentUser, teammates, ownerId1)
      const ownerLabel2 = getOwnerLabel(currentUser, teammates, ownerId2)
      return ownerLabel1.localeCompare(ownerLabel2)
    },
    [clients, teammates, currentUser],
  )

  const sortCrossClientResultsByMatch = useCallback((result1: CrossClientQueryResultDisplay, result2: CrossClientQueryResultDisplay) => {
    return matchOrderMap[result1.match] - matchOrderMap[result2.match]
  }, [])

  return { sortCrossClientResultsByClient, sortCrossClientResultsByOwner, sortCrossClientResultsByMatch }
}
